import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api, ApiError, jsonBody } from './api'
import { useAuth } from './auth'
import { DotItBrand } from './brand'
import { Captcha } from './captcha'
import type { SessionResponse } from './types'

function AuthFrame({ children }: { children: React.ReactNode }) {
  return (
    <main className='relative grid min-h-svh place-items-center overflow-hidden bg-zinc-950 px-4 py-12 text-zinc-50'>
      <div className='absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(239,68,68,.18),transparent_34%),radial-gradient(circle_at_85%_80%,rgba(255,255,255,.08),transparent_30%)]' />
      <div className='relative w-full max-w-md'>
        <Link
          to='/'
          className='mb-7 inline-flex'
          aria-label='DOT IT Signage home'
        >
          <DotItBrand className='[&_span]:text-zinc-50' />
        </Link>
        {children}
        <p className='mt-6 text-center text-xs text-zinc-500'>
          Secure content operations for every screen.
        </p>
      </div>
    </main>
  )
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError
    ? error.message
    : 'Something went wrong. Please try again.'
}

export function LoginPage() {
  const { session, establish } = useAuth()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [challengeId, setChallengeId] = useState<string>()
  const [formError, setFormError] = useState('')
  if (session) {
    return <Navigate to='/app' replace />
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setBusy(true)
    setFormError('')
    const data = new FormData(event.currentTarget)
    try {
      if (challengeId) {
        const result = await api<SessionResponse>('/auth/mfa/verify', {
          method: 'POST',
          body: jsonBody({
            challengeId,
            code: String(data.get('code') ?? ''),
          }),
        })
        establish(result)
        navigate('/app')
      } else {
        const result = await api<
          SessionResponse | { mfaRequired: true; challengeId: string }
        >('/auth/login', {
          method: 'POST',
          body: jsonBody({
            email: data.get('email'),
            password: data.get('password'),
            remember: data.get('remember') === 'on',
          }),
        })
        if ('mfaRequired' in result) {
          setChallengeId(result.challengeId)
        } else {
          establish(result)
          navigate(result.mfaEnrollmentRequired ? '/app/security' : '/app')
        }
      }
    } catch (error) {
      const message = errorMessage(error)
      setFormError(message)
      toast.error(message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthFrame>
      <Card className='border-white/10 bg-white text-zinc-950 shadow-2xl'>
        <CardHeader>
          <CardTitle>
            {challengeId ? 'Verify your identity' : 'Welcome back'}
          </CardTitle>
          <CardDescription>
            {challengeId
              ? 'Enter the six-digit code from your authenticator.'
              : 'Sign in to manage your screen network.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className='space-y-4' onSubmit={submit}>
            {challengeId ? (
              <div className='space-y-2'>
                <Label htmlFor='code'>Authenticator code</Label>
                <Input
                  id='code'
                  name='code'
                  inputMode='numeric'
                  autoComplete='one-time-code'
                  pattern='[0-9]{6}'
                  maxLength={6}
                  required
                  autoFocus
                />
              </div>
            ) : (
              <>
                <div className='space-y-2'>
                  <Label htmlFor='email'>Email</Label>
                  <Input
                    id='email'
                    name='email'
                    type='email'
                    autoComplete='email'
                    required
                    autoFocus
                  />
                </div>
                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <Label htmlFor='password'>Password</Label>
                    <Link
                      className='text-xs text-red-600 hover:underline'
                      to='/forgot-password'
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id='password'
                    name='password'
                    type='password'
                    autoComplete='current-password'
                    required
                  />
                </div>
                <label className='flex items-center gap-2 text-sm'>
                  <input
                    name='remember'
                    type='checkbox'
                    className='size-4 accent-red-500'
                  />
                  Keep me signed in on this device
                </label>
              </>
            )}
            <Button
              className='w-full bg-red-500 text-white hover:bg-red-600'
              disabled={busy}
            >
              {busy ? 'Please wait…' : challengeId ? 'Verify' : 'Sign in'}
            </Button>
            {formError && (
              <div
                className='rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700'
                role='alert'
              >
                <p>{formError}</p>
                {!challengeId && (
                  <Link
                    className='mt-1 inline-block font-medium underline underline-offset-2'
                    to='/forgot-password'
                  >
                    Reset your password
                  </Link>
                )}
              </div>
            )}
          </form>
          {!challengeId && (
            <p className='mt-5 text-center text-sm text-muted-foreground'>
              New to DOT IT Signage?{' '}
              <Link
                to='/signup'
                className='font-medium text-red-600 hover:underline'
              >
                Start your 14-day trial
              </Link>
            </p>
          )}
        </CardContent>
      </Card>
    </AuthFrame>
  )
}

export function SignupPage() {
  const [captchaToken, setCaptchaToken] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const onCaptcha = useCallback((token: string) => setCaptchaToken(token), [])

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setBusy(true)
    const data = new FormData(event.currentTarget)
    try {
      await api('/auth/signup', {
        method: 'POST',
        body: jsonBody({
          name: data.get('name'),
          organizationName: data.get('organizationName'),
          email: data.get('email'),
          password: data.get('password'),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          captchaToken,
        }),
      })
      setSent(true)
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthFrame>
      <Card className='border-white/10 bg-white text-zinc-950 shadow-2xl'>
        <CardHeader>
          <CardTitle>
            {sent ? 'Check your inbox' : 'Start your free trial'}
          </CardTitle>
          <CardDescription>
            {sent
              ? 'Use the verification link we sent to activate your workspace.'
              : 'Three screens for 14 days. No card required.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <Button asChild variant='outline' className='w-full'>
              <Link to='/login'>Return to sign in</Link>
            </Button>
          ) : (
            <form className='space-y-4' onSubmit={submit}>
              <div className='grid grid-cols-2 gap-3'>
                <div className='space-y-2'>
                  <Label htmlFor='name'>Your name</Label>
                  <Input id='name' name='name' autoComplete='name' required />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='organizationName'>Organization</Label>
                  <Input
                    id='organizationName'
                    name='organizationName'
                    autoComplete='organization'
                    required
                  />
                </div>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='email'>Work email</Label>
                <Input
                  id='email'
                  name='email'
                  type='email'
                  autoComplete='email'
                  required
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='password'>Password</Label>
                <Input
                  id='password'
                  name='password'
                  type='password'
                  minLength={12}
                  autoComplete='new-password'
                  required
                />
                <p className='text-xs text-muted-foreground'>
                  Use at least 12 characters.
                </p>
              </div>
              <Captcha onToken={onCaptcha} />
              <Button
                className='w-full bg-red-500 text-white hover:bg-red-600'
                disabled={busy || !captchaToken}
              >
                {busy ? 'Creating workspace…' : 'Create workspace'}
              </Button>
              <p className='text-center text-xs text-muted-foreground'>
                By continuing, you agree to the service and privacy terms.
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </AuthFrame>
  )
}

export function VerifyEmailPage() {
  const { establish } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [message, setMessage] = useState('Verifying your email…')
  useEffect(() => {
    const token = new URLSearchParams(location.search).get('token')
    if (!token) {
      setMessage('The verification token is missing.')
      return
    }
    void api<SessionResponse>('/auth/verify-email', {
      method: 'POST',
      body: jsonBody({ token }),
    })
      .then((result) => {
        establish(result)
        navigate(result.mfaEnrollmentRequired ? '/app/security' : '/app', {
          replace: true,
        })
      })
      .catch((error: unknown) => setMessage(errorMessage(error)))
  }, [establish, location.search, navigate])
  return (
    <AuthFrame>
      <Card className='bg-white text-zinc-950'>
        <CardHeader>
          <CardTitle>Email verification</CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
      </Card>
    </AuthFrame>
  )
}

export function PasswordPage({ reset = false }: { reset?: boolean }) {
  const location = useLocation()
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setBusy(true)
    const data = new FormData(event.currentTarget)
    try {
      if (reset) {
        const token = new URLSearchParams(location.search).get('token')
        await api('/auth/password/reset', {
          method: 'POST',
          body: jsonBody({ token, password: data.get('password') }),
        })
      } else {
        await api('/auth/password/forgot', {
          method: 'POST',
          body: jsonBody({ email: data.get('email') }),
        })
      }
      setSent(true)
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  return (
    <AuthFrame>
      <Card className='bg-white text-zinc-950'>
        <CardHeader>
          <CardTitle>
            {reset ? 'Choose a new password' : 'Reset your password'}
          </CardTitle>
          <CardDescription>
            {sent
              ? reset
                ? 'Your password has been changed. Sign in again.'
                : 'If the account is eligible, a reset link is on its way.'
              : reset
                ? 'Use at least 12 characters. Other sessions will be revoked.'
                : 'We will send a one-hour reset link.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <Button asChild className='w-full'>
              <Link to='/login'>Return to sign in</Link>
            </Button>
          ) : (
            <form className='space-y-4' onSubmit={submit}>
              {reset ? (
                <div className='space-y-2'>
                  <Label htmlFor='password'>New password</Label>
                  <Input
                    id='password'
                    name='password'
                    type='password'
                    minLength={12}
                    required
                  />
                </div>
              ) : (
                <div className='space-y-2'>
                  <Label htmlFor='email'>Email</Label>
                  <Input id='email' name='email' type='email' required />
                </div>
              )}
              <Button className='w-full' disabled={busy}>
                {busy ? 'Please wait…' : 'Continue'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </AuthFrame>
  )
}
